import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SendIcon, CheckCircleIcon } from 'lucide-react';
import { useForm } from '@formspree/react';
import { motion } from 'framer-motion';
export function ContactForm() {
  const {
    t
  } = useTranslation();
  const [state, handleSubmit] = useForm('movpjowb');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    message: ''
  });
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit(e);
    if (!state.errors) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        course: '',
        message: ''
      });
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const courses = ['Basic English Language', 'Intermediate English Language', 'Advanced English Language', 'IELTS Preparations', 'Diploma in English', 'Public Speaking', 'French Classes', 'Arabic Language', 'School Education (Class 1-12)', 'University Level', 'AI & ML', 'Networking', 'Cyber Security', 'Python', 'Java', 'Other'];
  if (state.succeeded) {
    return <section className="w-full py-32 px-6 bg-gradient-to-b from-slate-50 to-white" data-id="element-27">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mx-auto" data-id="element-28">
          <div className="glass rounded-3xl shadow-2xl p-12 md:p-16 text-center" data-id="element-29">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200
          }} className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl" data-id="element-30">
              <CheckCircleIcon className="w-14 h-14 text-white" data-id="element-31" />
            </motion.div>
            <h2 className="text-4xl font-bold text-gradient mb-6" data-id="element-32">
              Thank You!
            </h2>
            <p className="text-xl text-slate-600 mb-10 font-light" data-id="element-33">
              Your inquiry has been sent successfully. We'll get back to you
              shortly.
            </p>
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => window.location.reload()} className="glass px-10 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl transition-all" data-id="element-34">
              <span className="text-gradient" data-id="element-35">Submit Another Inquiry</span>
            </motion.button>
          </div>
        </motion.div>
      </section>;
  }
  return <section className="w-full py-32 px-6 bg-gradient-to-b from-slate-50 to-white" data-id="element-36">
      <div className="max-w-4xl mx-auto" data-id="element-37">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-38">
          <h2 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-39">
            <span className="text-gradient" data-id="element-40">{t('contact.title')}</span>
          </h2>
          <p className="text-xl md:text-2xl text-slate-600 font-light" data-id="element-41">
            {t('contact.subtitle')}
          </p>
        </motion.div>
        <motion.form initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} onSubmit={handleSubmit} className="glass rounded-3xl shadow-2xl p-10 md:p-14" data-id="element-42">
          <div className="grid md:grid-cols-2 gap-8 mb-8" data-id="element-43">
            <div data-id="element-44">
              <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-45">
                {t('contact.name')}
              </label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-5 py-4 bg-white/50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all shadow-sm" data-id="element-46" />
            </div>
            <div data-id="element-47">
              <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-48">
                {t('contact.email')}
              </label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-5 py-4 bg-white/50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all shadow-sm" data-id="element-49" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-8" data-id="element-50">
            <div data-id="element-51">
              <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-52">
                {t('contact.phone')}
              </label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-5 py-4 bg-white/50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all shadow-sm" data-id="element-53" />
            </div>
            <div data-id="element-54">
              <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-55">
                {t('contact.course')}
              </label>
              <select name="course" value={formData.course} onChange={handleChange} required className="w-full px-5 py-4 bg-white/50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all shadow-sm" data-id="element-56">
                <option value="" data-id="element-57">{t('contact.selectCourse')}</option>
                {courses.map(course => <option key={course} value={course} data-id="element-58">
                    {course}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="mb-8" data-id="element-59">
            <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-60">
              {t('contact.message')}
            </label>
            <textarea name="message" value={formData.message} onChange={handleChange} rows={5} className="w-full px-5 py-4 bg-white/50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none shadow-sm" data-id="element-61" />
          </div>
          <motion.button type="submit" disabled={state.submitting} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="w-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-500 text-white font-semibold py-5 px-6 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl hover:shadow-2xl text-lg" data-id="element-62">
            <SendIcon className="w-6 h-6" data-id="element-63" />
            {state.submitting ? 'Sending...' : t('contact.submit')}
          </motion.button>
          {state.errors && state.errors.length > 0 && <p className="mt-6 text-red-600 text-center font-medium" data-id="element-64">
              There was an error sending your message. Please try again.
            </p>}
        </motion.form>
      </div>
    </section>;
}